import React from 'react';
import {
  blankPayrollAdjustmentForm,
  PayrollAdjustmentBookingDrawer,
  type PayrollAdjustmentBookingForm,
} from './PayrollAdjustmentBookingDrawer';
import type {
  PayrollAdjustmentCategory,
  PayrollAdjustmentEntryRow,
  PayrollAdjustmentRuleRow,
} from '../../../utils/payrollAdjustmentCategories';

type EmployeeOption = { id: string; name: string };

type UsePayrollAdjustmentDrawerOptions = {
  categories: PayrollAdjustmentCategory[];
  entries: PayrollAdjustmentEntryRow[];
  rules: PayrollAdjustmentRuleRow[];
  categoryByKey: Map<string, PayrollAdjustmentCategory>;
  employees: EmployeeOption[];
  defaultEmployeeId: string;
  defaultFromYmd: string;
  hideEmployeeField?: boolean;
  onReload: () => Promise<void>;
  onAfterSaveRecurring?: () => void;
};

export function usePayrollAdjustmentDrawer({
  categories,
  entries,
  rules,
  categoryByKey,
  employees,
  defaultEmployeeId,
  defaultFromYmd,
  hideEmployeeField = false,
  onReload,
  onAfterSaveRecurring,
}: UsePayrollAdjustmentDrawerOptions) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerTitle, setDrawerTitle] = React.useState('Neue Buchung');
  const [editingEntryId, setEditingEntryId] = React.useState<string | null>(null);
  const [editingRuleId, setEditingRuleId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<PayrollAdjustmentBookingForm>(() =>
    blankPayrollAdjustmentForm(defaultFromYmd, defaultEmployeeId),
  );
  const [saving, setSaving] = React.useState(false);

  const openAdd = React.useCallback(
    (recurring = false) => {
      setEditingEntryId(null);
      setEditingRuleId(null);
      setDrawerTitle(recurring ? 'Regelmäßige Buchung' : 'Neue Buchung');
      setForm(blankPayrollAdjustmentForm(defaultFromYmd, defaultEmployeeId, recurring));
      setDrawerOpen(true);
    },
    [defaultEmployeeId, defaultFromYmd],
  );

  const openEditEntry = React.useCallback(
    (entryId: string) => {
      const entry = entries.find((e) => e.id === entryId);
      if (!entry) return;
      const cat = categoryByKey.get(entry.category_key);
      setEditingEntryId(entryId);
      setEditingRuleId(null);
      setDrawerTitle('Buchung bearbeiten');
      setForm({
        type: cat?.type ?? 'abzug',
        employee_id: entry.employee_id,
        category_key: entry.category_key,
        amount: String(entry.amount).replace('.', ','),
        note: entry.note,
        recurring: false,
        booking_date: entry.booking_date,
        day_of_month: Number(entry.booking_date.slice(8, 10)) || 15,
        start_year_month: entry.year_month,
        end_mode: 'exit',
        end_year_month: entry.year_month,
      });
      setDrawerOpen(true);
    },
    [categoryByKey, entries],
  );

  const openEditRule = React.useCallback(
    (ruleId: string) => {
      const rule = rules.find((r) => r.id === ruleId);
      if (!rule) return;
      const cat = categoryByKey.get(rule.category_key);
      setEditingRuleId(ruleId);
      setEditingEntryId(null);
      setDrawerTitle('Regelmäßige Buchung bearbeiten');
      setForm({
        type: cat?.type ?? 'abzug',
        employee_id: rule.employee_id,
        category_key: rule.category_key,
        amount: String(rule.amount).replace('.', ','),
        note: rule.note,
        recurring: true,
        booking_date: '',
        day_of_month: rule.day_of_month,
        start_year_month: rule.start_year_month,
        end_mode: rule.end_mode,
        end_year_month: rule.end_year_month ?? rule.start_year_month,
      });
      setDrawerOpen(true);
    },
    [categoryByKey, rules],
  );

  const saveDrawer = React.useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        employee_id: form.employee_id,
        category_key: form.category_key,
        amount: form.amount,
        note: form.note,
      };
      if (form.recurring) {
        const rulePayload = {
          ...payload,
          start_year_month: form.start_year_month,
          end_mode: form.end_mode,
          end_year_month: form.end_mode === 'date' ? form.end_year_month : null,
          day_of_month: form.day_of_month,
        };
        const url = editingRuleId
          ? `/api/employees/payroll-adjustments/rules/${editingRuleId}`
          : '/api/employees/payroll-adjustments/rules';
        const res = await fetch(url, {
          method: editingRuleId ? 'PATCH' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rulePayload),
        });
        const json = (await res.json()) as { error?: string };
        if (!res.ok) throw new Error(json.error || 'Speichern fehlgeschlagen.');
        onAfterSaveRecurring?.();
      } else {
        const entryPayload = { ...payload, booking_date: form.booking_date };
        const url = editingEntryId
          ? `/api/employees/payroll-adjustments/entries/${editingEntryId}`
          : '/api/employees/payroll-adjustments/entries';
        const res = await fetch(url, {
          method: editingEntryId ? 'PATCH' : 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryPayload),
        });
        const json = (await res.json()) as { error?: string };
        if (!res.ok) throw new Error(json.error || 'Speichern fehlgeschlagen.');
      }
      setDrawerOpen(false);
      await onReload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [editingEntryId, editingRuleId, form, onAfterSaveRecurring, onReload]);

  const deleteEntry = React.useCallback(
    async (entryId: string) => {
      if (!window.confirm('Buchung wirklich löschen?')) return;
      try {
        const res = await fetch(`/api/employees/payroll-adjustments/entries/${entryId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const json = (await res.json()) as { error?: string };
        if (!res.ok) throw new Error(json.error || 'Löschen fehlgeschlagen.');
        await onReload();
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
      }
    },
    [onReload],
  );

  const deleteRule = React.useCallback(
    async (ruleId: string) => {
      if (!window.confirm('Regelmäßige Buchung wirklich löschen?')) return;
      try {
        const res = await fetch(`/api/employees/payroll-adjustments/rules/${ruleId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const json = (await res.json()) as { error?: string };
        if (!res.ok) throw new Error(json.error || 'Löschen fehlgeschlagen.');
        await onReload();
      } catch (e) {
        window.alert(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
      }
    },
    [onReload],
  );

  const drawer = (
    <PayrollAdjustmentBookingDrawer
      open={drawerOpen}
      title={drawerTitle}
      employees={employees}
      categories={categories}
      editingCategoryKey={
        editingEntryId
          ? entries.find((e) => e.id === editingEntryId)?.category_key ?? null
          : editingRuleId
            ? rules.find((r) => r.id === editingRuleId)?.category_key ?? null
            : null
      }
      form={form}
      saving={saving}
      hideEmployeeField={hideEmployeeField}
      onClose={() => setDrawerOpen(false)}
      onSave={() => void saveDrawer()}
      onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
    />
  );

  return {
    openAdd,
    openEditEntry,
    openEditRule,
    deleteEntry,
    deleteRule,
    drawer,
  };
}
